import React, { useState, useEffect } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import dayjs from "dayjs";
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field, FieldRow } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import LogoSpinner from '../components/ui/LogoSpinner';
import FadeIn from '../components/ui/FadeIn';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';

const SCOPES = [
  { value: "full", label: "Full Access", description: "Access to all API endpoints" },
  { value: "pilot_app", label: "Pilot App", description: "Limited to /api/pilot and /api/reports" },
  { value: "flasher", label: "Flasher", description: "Drone flasher and the kiwipi station: /api/flasher, /api/flash, and minting a drone serial" },
  { value: "monday", label: "Monday.com", description: "Limited to /api/events/from-monday" },
  { value: "nas", label: "NAS Flight Analyst", description: "Create-only access to analysis results, warnings, and memories" },
  { value: "test_jig", label: "Test Jig", description: "Badge-validate + POST maintenance logs (operator_id required). For automated test rigs." },
  { value: "release", label: "Release", description: "Publishes the scanner app to the Firmware Hub and reads its latest build. For ./deploy.sh." },
];

const SCOPE_OPTIONS = SCOPES.map((s) => ({ key: s.value, label: s.label, hint: s.description }));
const SCOPE_LABEL_OPTIONS = SCOPES.map((s) => ({ key: s.value, label: s.label }));

const APIKeys = () => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [newScope, setNewScope] = useState("full");
  const [newExpiry, setNewExpiry] = useState("");
  const [newUserId, setNewUserId] = useState("");

  // Edit form state
  const [editingKey, setEditingKey] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editScope, setEditScope] = useState("full");
  const [editExpiry, setEditExpiry] = useState("");
  const [editUserId, setEditUserId] = useState("");

  // For displaying newly created tokens
  const [newToken, setNewToken] = useState(null);

  const fetchKeys = async () => {
    try {
      const data = await api.get("/admin/apikeys");
      setKeys(data || []);
    } catch (error) {
      console.error("Error fetching API keys:", error);
      toast.error("Failed to load API keys");
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get("/admin/apikeys/assignable-users");
      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchKeys(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreate = async () => {
    if (!newDescription.trim()) {
      toast.error("Description is required");
      return;
    }
    if (!newUserId) {
      toast.error("User assignment is required");
      return;
    }

    setSaving(true);
    try {
      const result = await api.post("/admin/apikeys", {
        body: {
          description: newDescription.trim(),
          scope: newScope,
          expiry: newExpiry || null,
          userId: newUserId,
        },
      });

      setNewToken(result.fullToken);
      toast.success("API key created successfully");
      setShowCreateForm(false);
      resetCreateForm();
      fetchKeys();
    } catch (error) {
      console.error("Error creating API key:", error);
      const errorMsg = error?.error?.message || error?.message || "Failed to create API key";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const resetCreateForm = () => {
    setNewDescription("");
    setNewScope("full");
    setNewExpiry("");
    setNewUserId("");
  };

  const startEdit = (key) => {
    setEditingKey(key.id);
    setEditDescription(key.description || "");
    setEditScope(key.scope || "full");
    setEditExpiry(key.expiry ? dayjs(key.expiry).format("YYYY-MM-DD") : "");
    setEditUserId(key.user_id || "");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditDescription("");
    setEditScope("full");
    setEditExpiry("");
    setEditUserId("");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/apikeys/${editingKey}`, {
        body: {
          description: editDescription,
          scope: editScope,
          expiry: editExpiry || null,
          userId: editUserId || null,
        },
      });
      toast.success("API key updated successfully");
      cancelEdit();
      fetchKeys();
    } catch (error) {
      console.error("Error updating API key:", error);
      const errorMsg = error?.error?.message || error?.message || "Failed to update API key";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (key) => {
    const confirmMsg = `Are you sure you want to regenerate the token for "${key.description || "this key"}"?\n\nThe old token will immediately stop working.`;

    if (!(await confirm({ title: "Regenerate token", message: confirmMsg, confirmLabel: "Regenerate", tone: "danger" }))) return;

    setSaving(true);
    try {
      const result = await api.post(`/admin/apikeys/${key.id}/regenerate`);
      setNewToken(result.fullToken);
      toast.success("Token regenerated successfully");
      fetchKeys();
    } catch (error) {
      console.error("Error regenerating API key:", error);
      const errorMsg = error?.error?.message || error?.message || "Failed to regenerate token";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    const confirmMsg = `Are you sure you want to delete the API key "${key.description || key.maskedToken}"?\n\nThis action cannot be undone.`;

    if (!(await confirm({ title: "Delete API key", message: confirmMsg, confirmLabel: "Delete", tone: "danger" }))) return;

    setSaving(true);
    try {
      await api.delete(`/admin/apikeys/${key.id}`);
      toast.success("API key deleted successfully");
      fetchKeys();
    } catch (error) {
      console.error("Error deleting API key:", error);
      const errorMsg = error?.error?.message || error?.message || "Failed to delete API key";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const isExpired = (expiry) => {
    if (!expiry) return false;
    return dayjs(expiry).isBefore(dayjs());
  };

  if (loading) return <LogoSpinner label="Loading API keys" />;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">API Key Management</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className={buttonClass('filled')}
        >
          + New API Key
        </button>
      </div>

      {/* New Token Display Modal */}
      <Dialog
        open={!!newToken}
        onClose={() => setNewToken(null)}
        title="New API key created"
        size="lg"
        dismissible={false}
        closeOnEscape={false}
        footer={
          <>
            <button type="button" onClick={() => setNewToken(null)} className={buttonClass('quiet')}>
              Close
            </button>
            <button type="button" onClick={() => copyToClipboard(newToken)} className={buttonClass('filled')}>
              Copy to Clipboard
            </button>
          </>
        }
      >
        <p className="text-on-surface mb-4">
          Copy this token now. You won't be able to see it again!
        </p>
        <div className="bg-surface-container-high p-4 rounded-control font-mono text-sm break-all text-green-400">
          {newToken}
        </div>
      </Dialog>

      {/* Create Form Modal */}
      <Dialog
        open={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
          resetCreateForm();
        }}
        title="Create New API Key"
        size="lg"
        dismissible={false}
        closeOnEscape={false}
        busy={saving}
        dirty={newDescription.trim() !== "" || newScope !== "full" || newExpiry !== "" || newUserId !== ""}
        onSubmit={handleCreate}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                resetCreateForm();
              }}
              className={buttonClass('quiet')}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={buttonClass('filled')}>
              {saving ? "Creating..." : "Create API Key"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FieldRow label="Description *">
            <Field
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="e.g., Battery integration, Flasher app"
            />
          </FieldRow>

          <FieldRow label="Scope">
            <Select
              label="Scope"
              value={newScope}
              onChange={setNewScope}
              options={SCOPE_OPTIONS}
            />
          </FieldRow>

          <FieldRow label="Expiry Date (optional)">
            <Field
              type="date"
              value={newExpiry}
              onChange={(e) => setNewExpiry(e.target.value)}
            />
          </FieldRow>

          <FieldRow label="Assign to User *">
            <Select
              label="Assign to user"
              value={newUserId}
              onChange={setNewUserId}
              placeholder="Select a user..."
              options={users.map((u) => ({
                key: String(u.id),
                label: `${u.firstname} ${u.lastname} ${u.is_service ? "(Service)" : "(Admin)"}`,
              }))}
            />
          </FieldRow>
        </div>
      </Dialog>

      {/* API Keys List */}
      <ListStack
        items={keys}
        getKey={(key) => String(key.id)}
        spacing="loose"
        label="API keys"
        empty={
          <EmptyState variant="area">
            No API keys found. Create one to get started.
          </EmptyState>
        }
        renderRow={(key) => (
          <ListRow tone={isExpired(key.expiry) ? "danger" : undefined}>
          <FadeIn key={editingKey === key.id ? "edit" : "view"}>
          {editingKey === key.id ? (
            /* Edit mode */
            <div className="space-y-3">
              <FieldRow label="Description">
                <Field
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </FieldRow>

              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Scope">
                  <Select
                    label="Scope"
                    value={editScope}
                    onChange={setEditScope}
                    options={SCOPE_LABEL_OPTIONS}
                  />
                </FieldRow>
                <FieldRow label="Expiry">
                  <Field
                    type="date"
                    value={editExpiry}
                    onChange={(e) => setEditExpiry(e.target.value)}
                  />
                </FieldRow>
              </div>

              <FieldRow label="Assigned User">
                <Select
                  label="Assigned user"
                  value={String(editUserId)}
                  onChange={setEditUserId}
                  options={[
                    { key: "", label: "No user assigned" },
                    ...users.map((u) => ({ key: String(u.id), label: `${u.firstname} ${u.lastname}` })),
                  ]}
                />
              </FieldRow>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className={buttonClass('filled')}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={cancelEdit}
                  className={buttonClass('quiet')}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Display mode */
            <div>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-white">
                      {key.description || "Unnamed Key"}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        key.scope === "full"
                          ? "bg-green-500/20 text-green-400"
                          : "bg-blue-500/20 text-blue-400"
                      }`}
                    >
                      {SCOPES.find((s) => s.value === key.scope)?.label || key.scope}
                    </span>
                    {isExpired(key.expiry) && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        Expired
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-on-surface-variant font-mono mt-1">
                    {key.maskedToken}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(key)}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleRegenerate(key)}
                    disabled={saving}
                    className={buttonClass('warn', 'sm')}
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={() => handleDelete(key)}
                    disabled={saving}
                    className={buttonClass('danger', 'sm')}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="text-xs text-on-surface-variant flex flex-wrap gap-x-4 gap-y-1">
                {key.userName && <span>User: {key.userName}</span>}
                {key.expiry && (
                  <span>
                    Expires: {dayjs(key.expiry).format("MMM D, YYYY")}
                  </span>
                )}
                {key.created_at && (
                  <span>
                    Created: {dayjs(key.created_at).format("MMM D, YYYY")}
                  </span>
                )}
                {key.last_used_at && (
                  <span className="text-green-500">
                    Last used: {dayjs(key.last_used_at).format("MMM D, YYYY h:mm A")}
                  </span>
                )}
                {key.request_count > 0 && (
                  <span>Requests: {key.request_count.toLocaleString()}</span>
                )}
              </div>
            </div>
          )}
          </FadeIn>
          </ListRow>
        )}
      />
    </PageLayout>
  );
};

export default APIKeys;
